import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { format } from 'date-fns';
import { Search, ChevronDown, ChevronRight, User, MapPin, Calendar, Smartphone, Monitor, Tablet, Globe, Trash2 } from 'lucide-react';

interface LayoutContext {
  viewAsStoreId?: string;
}

interface Customer {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  _count: { scans: number };
}

interface CustomerDetail {
  customer: {
    id: string;
    email: string;
    name: string;
    createdAt: string;
    totalScans: number;
  };
  participation: Array<{
    campaignName: string;
    runNumber: number;
    stores: Record<string, { name: string; scanCount: number }>;
    firstScan: string;
    lastScan: string;
    totalScans: number;
  }>;
  recentScans: Array<{
    id: string;
    scannedAt: string;
    store: { id: string; name: string };
    run: {
      id: string;
      instanceNumber: number;
      campaign: { id: string; name: string };
    };
    // Device info
    deviceType?: string;
    osName?: string;
    osVersion?: string;
    browserName?: string;
    browserVersion?: string;
    platform?: string;
    ipAddress?: string;
    city?: string;
    region?: string;
    country?: string;
  }>;
}

interface Store {
  id: string;
  name: string;
}

interface Campaign {
  id: string;
  name: string;
}

export default function CustomersPage() {
  const { viewAsStoreId } = useOutletContext<LayoutContext>() || {};
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState<string | null>(null);
  const [customerDetail, setCustomerDetail] = useState<CustomerDetail | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [storeName, setStoreName] = useState<string | null>(null);
  const [deletingCustomerId, setDeletingCustomerId] = useState<string | null>(null);

  // Use viewAsStoreId as default store filter
  const effectiveStoreFilter = viewAsStoreId || storeFilter;

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    // Get store name if viewing as store
    if (viewAsStoreId) {
      apiClient.get<{ success: boolean; data: { name: string } }>(`/admin/stores/${viewAsStoreId}`)
        .then(res => setStoreName(res.data.data.name))
        .catch(() => setStoreName(null));
    } else {
      setStoreName(null);
    }
  }, [viewAsStoreId]);

  useEffect(() => {
    loadCustomers();
  }, [search, effectiveStoreFilter, campaignFilter, yearFilter]);

  const loadFilters = async () => {
    try {
      const [storesRes, campaignsRes] = await Promise.all([
        apiClient.get<{ success: boolean; data: Store[] }>('/admin/stores'),
        apiClient.get<{ success: boolean; data: Campaign[] }>('/admin/campaigns'),
      ]);
      setStores(storesRes.data.data);
      setCampaigns(campaignsRes.data.data);
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  };

  const loadCustomers = async () => {
    setLoading(true);
    setCustomers([]);
    setExpandedCustomer(null);
    setCustomerDetail(null);

    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (effectiveStoreFilter) params.set('storeId', effectiveStoreFilter);
      if (campaignFilter) params.set('campaignId', campaignFilter);
      if (yearFilter) params.set('year', yearFilter);

      const response = await apiClient.get<{ success: boolean; data: Customer[] }>(
        `/admin/dashboard/customers?${params.toString()}`
      );
      setCustomers(response.data.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCustomer = async (customerId: string) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
      setCustomerDetail(null);
      return;
    }

    setExpandedCustomer(customerId);
    try {
      const response = await apiClient.get<{ success: boolean; data: CustomerDetail }>(
        `/admin/dashboard/customers/${customerId}`
      );
      setCustomerDetail(response.data.data);
    } catch (error) {
      console.error('Failed to load customer detail:', error);
    }
  };

  const handleDeleteCustomer = async (customerId: string, customerName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row expansion

    if (!confirm(`Are you sure you want to delete "${customerName}" and all their scan data? This action cannot be undone.`)) {
      return;
    }

    setDeletingCustomerId(customerId);
    try {
      await apiClient.delete(`/admin/dashboard/customers/${customerId}`);
      // Remove from list
      setCustomers(customers.filter(c => c.id !== customerId));
      // Close expanded view if this customer was expanded
      if (expandedCustomer === customerId) {
        setExpandedCustomer(null);
        setCustomerDetail(null);
      }
    } catch (error) {
      console.error('Failed to delete customer:', error);
      alert('Failed to delete customer. Please try again.');
    } finally {
      setDeletingCustomerId(null);
    }
  };

  const years = [2024, 2025, 2026, 2027];

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {storeName ? `${storeName} Customers` : 'Customers'}
        </h1>
        <span className="text-sm text-gray-500">{customers.length} customers</span>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${viewAsStoreId ? 'lg:grid-cols-4' : 'lg:grid-cols-5'}`}>
          <div className="lg:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          {/* Hide store filter when viewing as a specific store */}
          {!viewAsStoreId && (
            <select
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value)}
              className="input"
            >
              <option value="">All Stores</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          )}
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            className="input"
          >
            <option value="">All Campaigns</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
            ))}
          </select>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="input"
          >
            <option value="">All Years</option>
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="animate-pulse">Loading...</div>
      ) : customers.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">
          No customers found
        </div>
      ) : (
        <div className="space-y-2">
          {customers.map((customer) => (
            <div key={customer.id} className="card">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleCustomer(customer.id)}
              >
                <div className="flex items-center gap-4">
                  <button className="text-gray-400">
                    {expandedCustomer === customer.id ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{customer.name}</p>
                    <p className="text-sm text-gray-500">{customer.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{customer._count.scans} scans</p>
                    <p className="text-sm text-gray-500">
                      Joined {format(new Date(customer.createdAt), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleDeleteCustomer(customer.id, customer.name, e)}
                    disabled={deletingCustomerId === customer.id}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete customer"
                  >
                    {deletingCustomerId === customer.id ? (
                      <div className="w-5 h-5 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                    ) : (
                      <Trash2 className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Expanded Customer Detail */}
              {expandedCustomer === customer.id && customerDetail && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h4 className="font-medium text-gray-900 mb-3">Campaign Participation</h4>

                  {customerDetail.participation.length > 0 ? (
                    <div className="space-y-3 mb-6">
                      {customerDetail.participation.map((p, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{p.campaignName} (Run #{p.runNumber})</span>
                            <span className="text-sm text-gray-500">• {p.totalScans} scans</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <span>First scan: {format(new Date(p.firstScan), 'MMM d, yyyy h:mm a')}</span>
                            {p.firstScan !== p.lastScan && (
                              <span> • Last scan: {format(new Date(p.lastScan), 'MMM d, yyyy h:mm a')}</span>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {Object.values(p.stores).map((store, sIdx) => (
                              <span
                                key={sIdx}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-sm"
                              >
                                <MapPin className="w-3 h-3 text-gray-400" />
                                {store.name}: {store.scanCount}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm mb-4">No campaign participation data</p>
                  )}

                  <h4 className="font-medium text-gray-900 mb-3">Recent Scans</h4>
                  {customerDetail.recentScans.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500">
                            <th className="pb-2">Date/Time</th>
                            <th className="pb-2">Store</th>
                            <th className="pb-2">Campaign</th>
                            <th className="pb-2">Device</th>
                            <th className="pb-2">Location</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {customerDetail.recentScans.map((scan) => (
                            <tr key={scan.id}>
                              <td className="py-2">
                                {format(new Date(scan.scannedAt), 'MMM d, yyyy h:mm a')}
                              </td>
                              <td className="py-2">{scan.store.name}</td>
                              <td className="py-2">
                                {scan.run.campaign.name} (Run #{scan.run.instanceNumber})
                              </td>
                              <td className="py-2">
                                <div className="flex items-center gap-2">
                                  {scan.deviceType === 'mobile' && <Smartphone className="w-4 h-4 text-gray-400" />}
                                  {scan.deviceType === 'tablet' && <Tablet className="w-4 h-4 text-gray-400" />}
                                  {scan.deviceType === 'desktop' && <Monitor className="w-4 h-4 text-gray-400" />}
                                  {!scan.deviceType && <Globe className="w-4 h-4 text-gray-400" />}
                                  <div className="text-xs">
                                    {scan.osName && (
                                      <span className="text-gray-700">
                                        {scan.osName}
                                        {scan.osVersion && ` ${scan.osVersion}`}
                                      </span>
                                    )}
                                    {scan.browserName && (
                                      <span className="text-gray-500 ml-1">
                                        {scan.browserName}
                                        {scan.browserVersion && ` ${scan.browserVersion.split('.')[0]}`}
                                      </span>
                                    )}
                                    {!scan.osName && !scan.browserName && (
                                      <span className="text-gray-400">Unknown</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="py-2 text-xs text-gray-500">
                                {scan.city || scan.region || scan.country ? (
                                  <span>
                                    {[scan.city, scan.region, scan.country].filter(Boolean).join(', ')}
                                  </span>
                                ) : scan.ipAddress ? (
                                  <span className="text-gray-400">{scan.ipAddress}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No recent scans</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
